// Centralized analytics service — single entry point for all tracking destinations.
// All call sites import from here instead of directly from GTM/PostHog modules.
// When adding new destinations (Meta CAPI, TikTok, YouTube), add them here once.

import { initGTM, setGTMUserContext, clearGTMUserContext, pushToDataLayer, trackGTMPageView } from './gtm'
import { initPostHog, posthog } from './posthog'

export const analytics = {
  /**
   * Initialize all analytics providers. Call once at app startup (main.tsx).
   */
  init(): void {
    initPostHog()
    initGTM()
  },

  /**
   * Identify user across all providers. Call after successful auth sync.
   * Sets GTM user context (auto-injected into all dataLayer events)
   * and PostHog identity (user + workspace group).
   */
  identify(userId: string, workspaceId: string | undefined, email: string, workspaceName?: string): void {
    setGTMUserContext(userId, workspaceId, email)

    posthog.identify(userId, { email })
    if (workspaceId) {
      posthog.group('workspace', workspaceId, {
        name: workspaceName || workspaceId,
      })
    }
  },

  /**
   * Clear identity on sign-out. Prevents stale user data in subsequent events.
   */
  reset(): void {
    posthog.reset()
    clearGTMUserContext()
  },

  /**
   * Track a conversion event to all destinations.
   * PostHog-specific properties (e.g. $set_once) are harmlessly ignored by GTM.
   * Auto-attaches `environment` so PostHog insights can filter sandbox/dev noise.
   * Caller-supplied `environment` (if any) overrides the auto value.
   *
   * `opts.posthogOnlyData` merges extra properties into the PostHog capture but
   * NOT the GTM push — for user-typed free text (which can carry PII) that
   * product analytics needs but ad platforms (GA/Ads/Meta) must not receive.
   */
  track(
    event: string,
    data?: Record<string, unknown>,
    opts?: { posthogOnlyData?: Record<string, unknown> },
  ): void {
    const enriched = {
      environment: import.meta.env.VITE_ENV ?? 'development',
      ...data,
    }
    try {
      posthog.capture(
        event,
        opts?.posthogOnlyData ? { ...enriched, ...opts.posthogOnlyData } : enriched,
      )
    } catch { /* analytics non-critical */ }
    pushToDataLayer(event, enriched)
  },

  /**
   * Track SPA page view across all destinations.
   * PostHog uses '$pageview' event name; GTM uses 'page_view' with page_path.
   */
  trackPageView(path: string): void {
    posthog.capture('$pageview')
    trackGTMPageView(path)
  },
}
